import { NextPage } from "next";
import { useIntl } from "react-intl";
import Script from "next/script";
import { PageProps } from "@/pages/_app";
import Layout from "@/components/layout/Layout";
import HeroBlock, {
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";
import { SEO } from "@/interfaces/seo";
import { SEOWithTemplate } from "@/interfaces/seoWithTemplate";
import { injectPurifiedHTML } from "@/utils/injectPurifiedHTML";
import { Section } from "@/components/common/section/Section";
import {
  CategoryBlock,
  CategoryBlockProps,
} from "@/components/common/categoryBlock/CategoryBlock";

import { removeEmptyRel } from "@/utils/removeEmptyRel";
import { getNonEmptyString } from "@/utils/getNonEmptyString";
import {
  PointOfInterestList,
  PointOfInterestListProps,
} from "@/components/common/pointOfInterestList/PointOfInterestList";
import { FAQProps } from "@/utils/formatCategoryPage";
import { FAQ } from "@/components/pages/vehicle/categoryPage/faq/FAQ";
import vtImage from "@/public/images/vehicule_tourisme.png";
import vuImage from "@/public/images/vehicule_utilitaire.png";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1, {
  RentACarReview,
} from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import styles from "./Vehicle.module.scss";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type VehiclePageContent = {
  heroBlock: HeroBlockProps;
  vehicleType: VehicleType;
  firstParagraph: string;
  secondParagraph: string;
  pointOfInterestList?: PointOfInterestListProps;
  faq?: FAQProps;
  categoryBlock: CategoryBlockProps | undefined;
  otherVehiclesBlock: CategoryBlockProps | undefined;
  rentACarReview: RentACarReview | undefined;
  seo: SEO;
  seoWithTemplate: SEOWithTemplate;
};
type VehiclePageProps = PageProps &
  VehiclePageContent & {
    defaultCanonicalRoute: string;
  };

export const Vehicle: NextPage<VehiclePageProps> = ({
  layout,
  reviewsConst,
  heroBlock,
  vehicleType,
  firstParagraph,
  secondParagraph,
  pointOfInterestList,
  faq,
  categoryBlock,
  otherVehiclesBlock,
  rentACarReview,
  seo,
  seoWithTemplate,
  defaultCanonicalRoute,
}: VehiclePageProps) => {
  const intl = useIntl();

  const vehicleTypeLabel = intl.formatMessage({
    id:
      vehicleType === VehicleType.CAR ? "vehicleTypeCars" : "vehicleTypeTrucks",
  });

  const categoryPageTypeLabel =
    vehicleType === VehicleType.TRUCK ? "rentingTrucks" : "rentingCars";

  const urlIdentifier =
    vehicleType === VehicleType.TRUCK
      ? `vehicules-utilitaires`
      : `vehicules-tourisme`;

  return (
    <>
      <Layout
        layout={layout}
        defaultCanonicalRoute={defaultCanonicalRoute}
        pageMeta={seo}
        title={
          seoWithTemplate.overrideTitle ??
          intl.formatMessage(
            { id: "vehiclePageMetaTitle" },
            {
              vehicleType: getNonEmptyString(
                seoWithTemplate.valueInTemplateTitle,
                vehicleTypeLabel
              ),
            }
          )
        }
        description={
          seoWithTemplate.overrideDescription ??
          intl.formatMessage({ id: "vehiclePageMetaDescription" })
        }
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: categoryPageTypeLabel }),
            url: `/${urlIdentifier}`,
          },
        ]}
      >
        <Script
          id="insider-vehicle-page"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `
          window.insider_object = window.insider_object || {};
          window.insider_object.page = {
              "type": "Content"
          }
          `,
          }}
        />
        <HeroBlock
          {...heroBlock}
          searchBlockInitialState={{
            vehicleType,
            isOneWayTrip: false,
          }}
        />
        <Section>
          {firstParagraph && (
            <div
              className={styles.firstParagraph}
              dangerouslySetInnerHTML={injectPurifiedHTML(
                removeEmptyRel(firstParagraph)
              )}
            />
          )}
          {categoryBlock && <CategoryBlock {...categoryBlock} />}
          {pointOfInterestList && (
            <PointOfInterestList
              location={intl.formatMessage({ id: "pointsOfInterest" })}
              pointOfInterestList={pointOfInterestList}
            />
          )}
          {secondParagraph && (
            <div
              className={styles.secondParagraph}
              dangerouslySetInnerHTML={injectPurifiedHTML(
                removeEmptyRel(secondParagraph)
              )}
            />
          )}
          {otherVehiclesBlock && (
            <div className={styles.otherVehiclesBlockContainer}>
              <CategoryBlock {...otherVehiclesBlock} />
            </div>
          )}
        </Section>

        {faq && <FAQ {...faq} />}

        {rentACarReview && (
          <ReviewsBlock
            rentACarReview={rentACarReview}
            reviewsConst={reviewsConst}
            image={vehicleType === VehicleType.TRUCK ? vuImage : vtImage}
          />
        )}
      </Layout>
    </>
  );
};
